import { Config } from '@backstage/config';
import { DEFAULT_CATALOG_METADATA_FILENAME, Feature } from './helpers';

/**
 * Configuration helper for the backend.
 */
export class ConfigHelper {
  private readonly _config: Config;
  private readonly _backendUrl: string;
  private readonly _baseProxyUrl: string;
  private readonly _configPath: string;
  private readonly _baseApiUrl: string;
  private readonly _searchBackend: string | undefined;
  private constructor(config: Config) {
    this._config = config;
    this._backendUrl = this._config.getString('backend.baseUrl');
    this._baseProxyUrl = new URL('/api/proxy/', this._backendUrl).href;
    this._baseApiUrl = new URL('/api/', this._backendUrl).href;
    this._configPath =
      this._config.getOptionalString('configPath') ??
      DEFAULT_CATALOG_METADATA_FILENAME;

    this._searchBackend = this._config.getOptionalString('app.searchBackend');
    this._baseProxyUrl = new URL('/api/proxy/', this._backendUrl).href;
  }

  private static _instance: ConfigHelper;

  /**
   * Gets the singleton instance. Note that {@link ConfigHelper.init} must be called before this.
   */
  public static get instance() {
    if (!ConfigHelper._instance) {
      throw new Error('ConfigHelper not initialized.');
    }
    return ConfigHelper._instance;
  }

  /**
   * Determines if the app is running in devhub lite mode.
   */
  public static get isDevHubLite() {
    return (
      ConfigHelper.instance.config.getOptionalString('app.version') ===
      'devhub-lite'
    );
  }

  /**
   * The backend URL from the configuration.
   */
  public get backendUrl() {
    return this._backendUrl;
  }

  /**
   * The base proxy URL from the configuration.
   */
  public get searchBackend() {
    return this._searchBackend;
  }

  /**
   * Gets the external link mapping
   * expects a comma separated string of links
   * each link should be in the format of /template-id:/redirect-link
   */
  public get externalMarketplaceLinks() {
    const marketPlaceLinks = this._config.getOptionalString(
      'app.externalMarketplaceLinks',
    );
    return marketPlaceLinks ? marketPlaceLinks.split(',') : [];
  }

  /**
   * Gets the template redirect link mapping
   * expects a comma separated string of links
   * each link should be in the format of /template-link:/redirect-link
   */
  public get templateRedirectLinks() {
    const redirectLinksConfig = this._config.getOptionalString(
      'app.templateRouteRedirects',
    );
    return redirectLinksConfig ? redirectLinksConfig.split(',') : [];
  }

  /**
   * Gets the Slack feedback URL from app configuration.
   * Searches for the "Slack Channel" item in app.support.items by title and returns its first link URL.
   * The app.support structure is validated at startup via TypeScript config definitions.
   * @returns The Slack URL for feedback
   * @throws Error if "Slack Channel" item is not found in the support items array
   */
  public get slackFeedbackUrl(): string {
    const supportItems = this._config
      .getConfig('app.support')
      .getConfigArray('items');

    // Find the Slack Channel item by title
    const slackItem = supportItems.find(
      item => item.getString('title') === 'Slack Channel',
    );

    if (!slackItem) {
      throw new Error(
        'Slack Channel not found in app.support.items configuration',
      );
    }

    return slackItem.getConfigArray('links')[0].getString('url');
  }

  /**
   * Gets the system configuration
   */
  public get config(): Config {
    return this._config;
  }

  /**
   * Initializes the singleton.
   *
   * @example
   *     ConfigHelper.init(config);
   *
   * @param config - The configuration
   */
  public static init(config: Config): void {
    if (!ConfigHelper._instance) {
      ConfigHelper._instance = new ConfigHelper(config);
    }
  }

  /**
   * Checks if a config is present for a key and returns true if it is, false if not
   * @param config The config where to check the key
   * @param key The key to check for
   */
  public static has(config: Config, key: string) {
    try {
      return config.has(key);
    } catch (err) {
      return false;
    }
  }

  /**
   * Retrieves the proxy URL for the given path
   *
   * @param path The path to send through the proxy.
   */
  public proxyUrl(path: string): URL {
    return new URL(path.replace(/^\//, ''), this._baseProxyUrl);
  }

  /**
   * Retrieves the URL for the given API path
   * @param path The path to the api
   */
  public apiUrl(path: string): URL {
    return new URL(path.replace(/^\//, ''), this._baseApiUrl);
  }

  /**
   * Determines if the given feature is enabled.
   *
   * @param feature The feature being checked
   * @param defaultValue The default value if the feature is not configured
   * @param namespace The scope of the feature, either 'app' or 'backend'
   */
  public isFeatureEnabled(
    feature: Feature,
    defaultValue: boolean = true,
    namespace: string = 'app',
  ): boolean {
    const flagValue = this._config.getOptionalString(
      `${namespace}.features.${feature}`,
    );
    if (flagValue === undefined) {
      return defaultValue;
    }
    return flagValue.toLowerCase() === 'enabled';
  }

  /**
   * Determines if the given feature is enabled.
   *
   * @param feature The feature being checked
   * @param fn The function to execute if the feature is enabled
   * @param defaultValue The the value to return if the feature is NOT enabled
   */
  public async ifFeatureEnabled<TReturn>(
    feature: Feature,
    fn: () => Promise<TReturn>,
    defaultValue: TReturn,
  ): Promise<TReturn> {
    const flagValue = this.isFeatureEnabled(feature, true);
    if (flagValue) {
      return await fn();
    }
    return defaultValue;
  }

  /**
   * Constructs LocationUrl from repo html_url
   *
   * @param html_url html_url from the GitHub app_repo object. typically of the from https://github.comcast.com/orgname/reponame
   * @param default_branch default branch from GitHub app_repo object. if not present defaults to '-'
   * @returns url reference with branch information
   */
  public createLocationUrl(html_url: string, default_branch: string): string {
    return new URL(
      `blob/${default_branch}/${this._configPath}`,
      `${html_url}/`.replace(/\/+$/, '/'),
    ).href;
  }

  /**
   * Gets the service account OAuth token.
   * @returns The service account OAuth token.
   */
  public getServiceAccount(): string {
    return this._config.getString('backend.serviceAccountOAuth');
  }
}
